import {
  React,
  useMemo, useState, useEffect,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TableFooter,
  IconButton, AddCommentIcon, CommentIcon, KeyboardArrowUp, KeyboardArrowDown,
  Box, Paper, Typography,
  dayjs, Dayjs,
  Dialog, DataGrid, GridColDef, GridRenderCellParams,
  getStickyStyle,
  forwardRef, useImperativeHandle,
  useApi,
  Toaster,
  ForecastComment, AddBulkOutstandingComment,
  formatCurrency,
  parseMonthYearToUTCDate, ForecastDisplayType,
  RemoveCircleIcon, AddCircleIcon,
  Button,
  // OpenInFullIcon, CloseFullscreenIcon

} from '../../../utils/globalImports';

import { FeeStage, ProjectForecast, FeeStageComment, OutstandingRows } from '../../../interfaces/forecast.interface';
import { ApiResponse } from '../../../services/apiService';
import ResizeHandle from '../../Common/ResizeHandle';
import ThreadForecastCommentModal from '../../BaseLine/ThreadForecastCommentModal';
type ColWidths = ReturnType<typeof useColWidths>[0];

// const useColWidths = () => {
//   return useState({
//     expand: 50,
//     second: 50,
//     code: 100,
//     item: 100,
//     cost: 100,
//     quantity: 100,
//     totalCost: 100,
//     outstanding: 100,
//     thirdPartyCost: 100,
//   });
// };

const useColWidths = (months: string[]) => {
  return useState(() => {
    const baseWidths: Record<string, number> = {
      expand: 50,
      second: 50,
      code: 100,
      item: 100,
      cost: 100,
      quantity: 100,
      totalCost: 100,
      outstanding: 100,
      thirdPartyCost: 100,
    };

    months.forEach((m) => {
      baseWidths[m] = 150; // fixed width for each month col
    });

    return baseWidths;
  });
};



const commonHeaderStyle = {
  '& th': {
    fontSize: '16px',
    fontWeight: 'bold',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    backgroundColor: '#92d050',
  },
};

const commonRowStyle = {
  '& td': {
    fontSize: '14px',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    color: '#1e66ab'
  },
};

interface ModalProps {
  forecastData: ProjectForecast[];
  startDate: string;
  endDate: string;
  forecastType: number;
  readOnly: boolean;
}

interface CustomForecastRef {
  triggerSave: () => void;
}

interface CollapsibleRowProps {
  row: ProjectForecast;
  monthColumns: GridColDef[];
  rowStyle: any;
  monthHeaders: any[];
  onForecastChange: (feeStageID: number, month: string, value: number) => void;
  flattenedForecastMap: Record<number, Record<string, number> & FeeStage>;
  setToast: React.Dispatch<React.SetStateAction<{ open: boolean; message: string; type: string }>>;
  handleCommentClick: (feestage: FeeStageComment) => void;
  forecastType?: number;
  readOnly: boolean;
  colWidths: ColWidths;
  getLeftOffset: (key: keyof ColWidths, forecastType?: ForecastDisplayType) => number; // <-- also here
}

const generateMonthHeaders = (startDate: Dayjs, endDate: Dayjs): string[] => {
const monthHeaders: string[] = [];
  let current = startDate.startOf('month');
  
  while (current.isBefore(endDate) || current.isSame(endDate, 'month')) {
    monthHeaders.push(current.format('MMM-YYYY')); // e.g., "Aug-2024"
    current = current.add(1, 'month');
  }
  
  return monthHeaders;
};

// 2. Flatten forecasts into top-level fields
const flattenForecasts = (feestage: FeeStage) => {
  const forecastMap: { [key: string]: number } = {};

  feestage.forecasts.forEach(f => {
    const monthKey = dayjs(f.monthDate).format('MMM-YYYY');
    forecastMap[monthKey] = f.forecastValue;
  });

  return {
    ...feestage,
    ...forecastMap,
  };
};

// 3. Generate columns for each forecast month
export const getMonthColumns = (
  monthHeaders: string[],
  onCommentClick: (feestage: FeeStageComment) => void,
  readOnly?: boolean,
  colWidths: Record<string, number> = {}
): GridColDef[] =>
  monthHeaders.map((month) => ({
    field: month,
    headerName: month,
    //minWidth: 150,
    minWidth: colWidths[month] ?? 150,   // 👈 use colWidths here
    width: colWidths[month] ?? 150,
    editable: true,
    type: 'number',
    isLocked: (row: any) => {
      const forecasts = row.forecasts ?? [];
      const forecast = forecasts.find((f: any) => {
        const formattedMonth = dayjs(f.monthDate).format('MMM-YYYY');
        return formattedMonth === month;
      });
      return forecast ? !forecast.isInvoiced && !forecast.isAutoLock : true;
    },

    renderCell: (params: GridRenderCellParams) => {
      const value = params.row[month];
      const forecasts = params.row.forecasts ?? [];

      // Find the forecast that matches the current column month
      const forecast = forecasts.find((f: any) => {
        const formattedMonth = dayjs(f.monthDate).format('MMM-YYYY');
        return formattedMonth === month;
      });

      const isEnabled = forecast?.isEnabledViewComment;
      const isEditable = forecast ? !forecast.isInvoiced && !forecast.isAutoLock : true;

       // Hide value if invoiced but keep the column
      const displayValue = forecast?.isInvoiced ? '' : value?.toLocaleString() ?? 0;

      return (
        <Box sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 50, // ensures enough space for the icon
          width: '100%',
          ...(isEditable
            ? {}
            : {
              backgroundColor: '#fddede',
            }),
        }}>
          <Typography variant="body2">
            {displayValue}
          </Typography>

          <IconButton
            color="primary"
            size="small"
            disabled={readOnly}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
            }}
            onClick={() => {
              const comment: FeeStageComment = {
                id: params.row.feestageID,
                feeStageReferenceNumber: params.row.feeStageReferenceNumber,
                description: params.row.description,
                month,
                hasComments: isEnabled
              }
              onCommentClick(comment)
            }}
          >
            {isEnabled ? (
              <CommentIcon sx={{ color: '#1e66ab' }} />
            ) : (
              <AddCommentIcon sx={{ color: '#a3c63d' }} />
            )}
          </IconButton>
        </Box >
      );
    }
  }));


// const monthMap: Record<string, number> = {
//   Jan: 0, Feb: 1, Mar: 2, Apr: 3,
//   May: 4, Jun: 5, Jul: 6, Aug: 7,
//   Sep: 8, Oct: 9, Nov: 10, Dec: 11,
// };

// function parseMonthYearToUTCDate(input: string): string {
//   const [mon, yearStr] = input.split('-');
//   const year = parseInt(yearStr);
//   const monthIndex = monthMap[mon];
//   return new Date(Date.UTC(year, monthIndex, 1)).toISOString();
// }

// calculate total when page load 
const calculateTotals = (forecastData: ProjectForecast[]) => {
  let totalCost = 0;
  let totalQuantity = 0;
  let totalTotalCost = 0;
  let totalOutstanding = 0;
  let totalThirdPartyCost = 0;

  forecastData.forEach((proposal) => {
    proposal.categories?.forEach((category) => {
      category.subcategories?.forEach((subcat) => {
        subcat.feestages?.forEach((fee) => {
          totalCost += fee.cost ?? 0;
          totalQuantity += fee.quantity ?? 0;
          totalTotalCost += fee.totalCost ?? 0;
          totalOutstanding += fee.outstanding ?? 0;
          totalThirdPartyCost += fee.thirdPartyCost ?? 0;
        });
      });
    });
  });

  return {
    totalCost,
    totalQuantity,
    totalTotalCost,
    totalOutstanding,
    totalThirdPartyCost,
  };
};

// calculate Outstanding when update the month cell
const calculateTotalOutstanding = (map: Record<number, any>) => {
  return Object.values(map).reduce((sum, fee) => {
    return sum + (fee.outstanding ?? 0);
  }, 0);
};

const CollapsibleRow = ({
  row,
  monthColumns,
  rowStyle,
  monthHeaders,
  onForecastChange,
  flattenedForecastMap,
  setToast,
  handleCommentClick,
  forecastType,
  readOnly,
  colWidths,
  getLeftOffset
}: CollapsibleRowProps) => {

  const [openCategories, setOpenCategories] = useState<Record<number, boolean>>({});
  const [openSubcategories, setOpenSubcategories] = useState<Record<number, boolean>>({});

  return (
    <React.Fragment>
      {/* PROPOSAL ROW */}
      <TableRow sx={rowStyle}>
        <TableCell
          sx={{
            ...getStickyStyle(getLeftOffset("expand", forecastType), 50),
            minWidth: 50,
            width: 50,
            position: "sticky",
          }}
        />

        <TableCell
          sx={{
            ...getStickyStyle(getLeftOffset("second", forecastType), 50),
            minWidth: 50,
            width: 50,
            position: "sticky",
          }}
        />


        <TableCell
          sx={{
            ...getStickyStyle(getLeftOffset("code"), colWidths.code),
            minWidth: colWidths.code,
            width: colWidths.code,
          }}
        />
        <TableCell
          sx={{
            ...getStickyStyle(getLeftOffset("item"), colWidths.item),
            minWidth: colWidths.item,
            width: colWidths.item,
          }}
        >
          <Typography variant="h6" fontSize={25} fontWeight="bold">
            {row.proposalNumber}
          </Typography>
        </TableCell>
        {forecastType === ForecastDisplayType.ThirdPartyCost ? (
          <>
            <TableCell
              sx={{
                ...getStickyStyle(
                  getLeftOffset("thirdPartyCost", forecastType),
                  colWidths.cost + colWidths.quantity + colWidths.totalCost
                ),
                minWidth: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                width: colWidths.cost + colWidths.quantity + colWidths.totalCost,
              }}
            />
            <TableCell
              sx={{
                ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                minWidth: colWidths.outstanding,
                width: colWidths.outstanding,
              }}
            />
          </>
        ) : (
          <>
            <TableCell
              sx={{
                ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                minWidth: colWidths.cost,
                width: colWidths.cost,
              }}
            />
            <TableCell
              sx={{
                ...getStickyStyle(getLeftOffset("quantity"), colWidths.quantity),
                minWidth: colWidths.quantity,
                width: colWidths.quantity,
              }}
            />
            <TableCell
              sx={{
                ...getStickyStyle(getLeftOffset("totalCost"), colWidths.totalCost),
                minWidth: colWidths.totalCost,
                width: colWidths.totalCost,
              }}
            />
            <TableCell
              sx={{
                ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                minWidth: colWidths.outstanding,
                width: colWidths.outstanding,
              }}
            />
          </>
        )}
      </TableRow>

      {/* CATEGORY LEVEL */}
      {row.categories?.map((category) => {
        const categoryTotals = {
          cost: category.subcategories?.reduce(
            (sum, sub) =>
              sum +
              (sub.feestages?.reduce((s, f) => s + (f.cost || 0), 0) ?? 0),
            0
          ),
          quantity: category.subcategories?.reduce(
            (sum, sub) =>
              sum +
              (sub.feestages?.reduce((s, f) => s + (f.quantity || 0), 0) ?? 0),
            0
          ),
          totalCost: category.subcategories?.reduce(
            (sum, sub) =>
              sum +
              (sub.feestages?.reduce((s, f) => s + (f.totalCost || 0), 0) ?? 0),
            0
          ),
          thirdPartyCost: category.subcategories?.reduce(
            (sum, sub) =>
              sum +
              (sub.feestages?.reduce((s, f) => s + (f.thirdPartyCost || 0), 0) ?? 0),
            0
          ),
          outstanding: category.subcategories?.reduce(
            (sum, sub) =>
              sum +
              (sub.feestages?.reduce(
                (s, f) =>
                  s +
                  (flattenedForecastMap[f.feestageID]?.outstanding ??
                    f.outstanding ??
                    0),
                0
              ) ?? 0),
            0
          ),
        };
        return (
          <React.Fragment key={category.categoryID}>
            <TableRow sx={rowStyle}>
              <TableCell sx={{
                ...getStickyStyle(getLeftOffset("expand", forecastType), 50),
                minWidth: 50,
                width: 50,
                position: "sticky",
              }}>
                <IconButton
                  size="small"
                  onClick={() =>
                    setOpenCategories((prev) => ({
                      ...prev,
                      [category.categoryID]: !prev[category.categoryID],
                    }))
                  }
                >
                  {openCategories[category.categoryID] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              </TableCell>
              <TableCell sx={{
                ...getStickyStyle(getLeftOffset("second", forecastType), 50),
                minWidth: 50,
                width: 50,
                position: "sticky",
              }} />
              <TableCell
                sx={{
                  ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                  minWidth: colWidths.code,
                  width: colWidths.code,
                }}
              />
              <TableCell
                sx={{
                  ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                  minWidth: colWidths.item,
                  width: colWidths.item,
                }}
              >
                <Typography variant="h6" fontSize={18} fontWeight="bold">
                  {category.categoryName}
                </Typography>
              </TableCell>
              {forecastType === ForecastDisplayType.ThirdPartyCost ? (
                <>
                  <TableCell
                    sx={{
                      ...getStickyStyle(
                        getLeftOffset("thirdPartyCost", forecastType),
                        colWidths.cost + colWidths.quantity + colWidths.totalCost
                      ),
                      minWidth: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                      width: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                    }}
                  >
                    {!openCategories[category.categoryID] &&
                      formatCurrency(categoryTotals.thirdPartyCost)}
                  </TableCell>

                  <TableCell
                    sx={{
                      ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                      minWidth: colWidths.outstanding,
                      width: colWidths.outstanding,

                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        minHeight: 50,
                        width: "100%",
                        px: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "clip",
                      }}
                    >
                      {!openCategories[category.categoryID] &&
                        formatCurrency(categoryTotals.outstanding)}
                    </Box>
                  </TableCell>

                </>

              ) : (
                <>
                  <TableCell
                    sx={{
                      ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                      minWidth: colWidths.cost,
                      width: colWidths.cost,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {!openCategories[category.categoryID] &&
                      formatCurrency(categoryTotals.cost)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...getStickyStyle(getLeftOffset("quantity"), colWidths.quantity),
                      minWidth: colWidths.quantity,
                      width: colWidths.quantity,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {/* {!openCategories[category.categoryID] && categoryTotals.quantity} */}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...getStickyStyle(getLeftOffset("totalCost"), colWidths.totalCost),
                      minWidth: colWidths.totalCost,
                      width: colWidths.totalCost,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {!openCategories[category.categoryID] &&
                      formatCurrency(categoryTotals.totalCost)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                      minWidth: colWidths.outstanding,
                      width: colWidths.outstanding,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        minHeight: 50,
                        width: "100%",
                        px: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "clip",
                      }}
                    >
                      {!openCategories[category.categoryID] &&
                        formatCurrency(categoryTotals.outstanding)}
                    </Box>
                  </TableCell>
                </>
              )}

              {monthColumns.map((col) => {
                // sum forecasts for this month across ALL subcategories/feestages in this category
                const monthTotal = category.subcategories?.reduce((subSum, sub) => {
                  return (
                    subSum +
                    (sub.feestages?.reduce((feeSum, f) => {
                      const forecastRow =
                        flattenedForecastMap[f.feestageID] ?? flattenForecasts(f);
                      return feeSum + (forecastRow[col.field] || 0);
                    }, 0) ?? 0)
                  );
                }, 0);

                return (
                  <TableCell
                    key={col.field}
                    sx={{
                      minWidth: 150,
                      width: 150,
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    {!openCategories[category.categoryID] && formatCurrency(monthTotal)}
                  </TableCell>
                );
              })}


            </TableRow>

            {/* SUBCATEGORY LEVEL */}
            {openCategories[category.categoryID] &&
              category.subcategories?.map((subcategory) => (
                <React.Fragment key={subcategory.subcategoryID}>
                  {(() => {
                    const subcategoryTotals = {
                      cost: subcategory.feestages?.reduce((sum, f) => sum + (f.cost || 0), 0),
                      quantity: subcategory.feestages?.reduce((sum, f) => sum + (f.quantity || 0), 0),
                      totalCost: subcategory.feestages?.reduce((sum, f) => sum + (f.totalCost || 0), 0),
                      thirdPartyCost: subcategory.feestages?.reduce((sum, f) => sum + (f.thirdPartyCost || 0), 0),
                      outstanding: subcategory.feestages?.reduce(
                        (sum, f) => sum + (flattenedForecastMap[f.feestageID]?.outstanding ?? f.outstanding ?? 0),
                        0
                      ) ?? 0,
                      // month totals
                      monthTotals: monthColumns.reduce((acc, col) => {
                        const total = subcategory.feestages?.reduce((sum, f) => {
                          const forecastRow = flattenedForecastMap[f.feestageID] ?? flattenForecasts(f);
                          return sum + (forecastRow[col.field] || 0);
                        }, 0) ?? 0;
                        return { ...acc, [col.field]: total };
                      }, {} as Record<string, number>)
                    };
                    return (
                      <TableRow sx={rowStyle}>

                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("expand", forecastType), 50),
                            minWidth: 50,
                            width: 50,
                            position: "sticky",
                          }}
                        />

                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("second", forecastType), 50),
                            minWidth: 50,
                            width: 50,
                            position: "sticky",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              setOpenSubcategories((prev) => ({
                                ...prev,
                                [subcategory.subcategoryID]: !prev[subcategory.subcategoryID],
                              }))
                            }
                          >
                            {openSubcategories[subcategory.subcategoryID] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                            minWidth: colWidths.code,
                            width: colWidths.code,
                          }}
                        />
                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                            minWidth: colWidths.item,
                            width: colWidths.item,
                          }}
                        >
                          <Typography variant="h6" fontSize={14} fontWeight="bold">
                            {subcategory.subcategoryName}
                          </Typography>
                        </TableCell>
                            {forecastType === ForecastDisplayType.ThirdPartyCost ? (
                              <>
                                <TableCell
                                  sx={{
                                    ...getStickyStyle(
                                      getLeftOffset("thirdPartyCost", forecastType),
                                      colWidths.cost + colWidths.quantity + colWidths.totalCost
                                    ),
                                    minWidth: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                                    width: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                                  }}
                                > {!openSubcategories[subcategory.subcategoryID] && formatCurrency(subcategoryTotals.thirdPartyCost)}</TableCell>

                                <TableCell
                                  sx={{
                                    ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                                    minWidth: colWidths.outstanding,
                                    width: colWidths.outstanding,
                                  }}
                                >  {!openSubcategories[subcategory.subcategoryID] && formatCurrency(subcategoryTotals.outstanding)}</TableCell>
                              </>

                            ) : (
                              <>
                                <TableCell
                                  sx={{
                                    ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                                    minWidth: colWidths.cost,
                                    width: colWidths.cost,
                                  }}
                                > {!openSubcategories[subcategory.subcategoryID] && formatCurrency(subcategoryTotals.cost)}</TableCell>
                                <TableCell
                                  sx={{
                                    ...getStickyStyle(getLeftOffset("quantity"), colWidths.quantity),
                                    minWidth: colWidths.quantity,
                                    width: colWidths.quantity,
                                  }}
                                />
                                <TableCell
                                  sx={{
                                    ...getStickyStyle(getLeftOffset("totalCost"), colWidths.totalCost),
                                    minWidth: colWidths.totalCost,
                                    width: colWidths.totalCost,
                                  }}
                                > {!openSubcategories[subcategory.subcategoryID] && formatCurrency(subcategoryTotals.totalCost)}</TableCell>
                                <TableCell
                                  sx={{
                                    ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                                    minWidth: colWidths.outstanding,
                                    width: colWidths.outstanding,
                                  }}
                                >  {!openSubcategories[subcategory.subcategoryID] && formatCurrency(subcategoryTotals.outstanding)}</TableCell>

                                {monthColumns.map(col => (
                                  <TableCell key={col.field} sx={{ textAlign: "center", fontWeight: "bold" }}>
                                     {!openSubcategories[subcategory.subcategoryID] && formatCurrency(subcategoryTotals.monthTotals[col.field])}
                                  </TableCell>
                                ))}
                              </>
                            )}
                        

                      </TableRow>
                    );

                  })()}

                  {/* FEESTAGE LEVEL */}
                  {openSubcategories[subcategory.subcategoryID] &&
                    subcategory.feestages?.map((feestage) => (
                      <TableRow key={feestage.feestageID} sx={{
                        ...rowStyle,
                        ...(readOnly && {
                          backgroundColor: "#f0f0f0",   // grey background
                          color: "#9e9e9e",             // grey text
                          pointerEvents: "none",        // disable all interactions
                          opacity: 0.7,                 // make it look disabled
                        }),
                      }}>
                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("expand", forecastType), 50),
                            minWidth: 50,
                            width: 50,
                            position: "sticky",
                          }}
                        />

                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("second", forecastType), 50),
                            minWidth: 50,
                            width: 50,
                            position: "sticky",
                          }}
                        />
                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                            minWidth: colWidths.code,
                            width: colWidths.code,
                          }}
                        >{feestage.feeStageReferenceNumber}</TableCell>
                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                            minWidth: colWidths.item,
                            width: colWidths.item,
                          }}
                        >{feestage.description}</TableCell>

                        {forecastType === ForecastDisplayType.ThirdPartyCost ? (
                          <>
                            <TableCell
                              sx={{
                                ...getStickyStyle(
                                  getLeftOffset("thirdPartyCost", forecastType),
                                  colWidths.cost + colWidths.quantity + colWidths.totalCost
                                ),
                                minWidth: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                                width: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                              }}
                            >{formatCurrency(feestage.thirdPartyCost)}</TableCell>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                                minWidth: colWidths.outstanding,
                                width: colWidths.outstanding,
                              }}
                            >

                              <Box sx={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 50, // ensures enough space for the icon
                                width: '100%',
                              }}>
                                <Typography variant="body2">
                                  {
                                    formatCurrency(flattenedForecastMap[feestage.feestageID]?.outstanding
                                      ?? feestage.outstanding)
                                  }
                                </Typography>

                                {(() => {
                                  const isEnabled = flattenedForecastMap[feestage.feestageID]?.hasOutstandingComment;

                                  return (
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      disabled={readOnly}
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                      }}
                                      onClick={() => {
                                        const comment: FeeStageComment = {
                                          id: feestage.feestageID,
                                          feeStageReferenceNumber: feestage.feeStageReferenceNumber,
                                          description: feestage.description,
                                          month: 'Outstanding',
                                          hasComments: isEnabled,
                                        };
                                        handleCommentClick(comment);
                                      }}
                                    >
                                      {isEnabled ? (
                                        <CommentIcon sx={{ color: '#1e66ab' }} />
                                      ) : (
                                        <AddCommentIcon sx={{ color: '#a3c63d' }} />
                                      )}
                                    </IconButton>
                                  );
                                })()}

                              </Box >
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                                minWidth: colWidths.cost,
                                width: colWidths.cost,
                              }}
                            >{formatCurrency(feestage.cost)}</TableCell>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("quantity"), colWidths.quantity),
                                minWidth: colWidths.quantity,
                                width: colWidths.quantity,
                              }}
                            >{feestage.quantity}</TableCell>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("totalCost"), colWidths.totalCost),
                                minWidth: colWidths.totalCost,
                                width: colWidths.totalCost,
                              }}
                            >{formatCurrency(feestage.totalCost)}</TableCell>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                                minWidth: colWidths.outstanding,
                                width: colWidths.outstanding,
                                overflow: "hidden",
                              }}
                            >

                              <Box sx={{
                                //position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 50, // ensures enough space for the icon
                                width: '100%',
                                px: 1,
                              }}>
                                <Typography variant="body2"
                                  noWrap // prevents text from wrapping into icon space
                                  sx={{ flex: 1, textAlign: "center" }}>
                                  {
                                    formatCurrency(flattenedForecastMap[feestage.feestageID]?.outstanding
                                      ?? feestage.outstanding)
                                  }
                                </Typography>

                                {(() => {
                                  const isEnabled = flattenedForecastMap[feestage.feestageID]?.hasOutstandingComment;

                                  return (
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      disabled={readOnly}
                                      // sx={{
                                      //   position: 'absolute',
                                      //   top: 0,
                                      //   right: 0,
                                      // }}
                                      onClick={() => {
                                        const comment: FeeStageComment = {
                                          id: feestage.feestageID,
                                          feeStageReferenceNumber: feestage.feeStageReferenceNumber,
                                          description: feestage.description,
                                          month: 'Outstanding',
                                          hasComments: isEnabled,
                                        };
                                        handleCommentClick(comment);
                                      }}
                                    >
                                      {isEnabled ? (
                                        <CommentIcon sx={{ color: '#1e66ab' }} />
                                      ) : (
                                        <AddCommentIcon sx={{ color: '#a3c63d' }} />
                                      )}
                                    </IconButton>
                                  );
                                })()}

                              </Box >
                            </TableCell>
                          </>
                        )}
                        
                        {/* Monthly Forecast */}
                        <TableCell colSpan={monthHeaders.length} sx={{ padding: 0 }}>                        
                          <Box>
                            <Box
                              sx={{
                                display: 'inline-block',
                                minWidth: monthHeaders.length * 150,
                                verticalAlign: 'top',
                                '& .MuiDataGrid-root': { border: 'none' },
                                '& .MuiDataGrid-columnHeaders': {
                                  display: 'none',
                                  height: 0,
                                },
                                '& .MuiDataGrid-row': {
                                  maxHeight: 36,
                                  minHeight: 36,
                                },
                                '& .MuiDataGrid-cell': {
                                  fontSize: '14px',
                                  padding: '4px 8px',
                                  border: '1px solid rgba(0, 0, 0, 0.08)',
                                  color: '#1e66ab',
                                  whiteSpace: "nowrap",      // ✅ prevent text wrap
                                  overflow: "hidden",        // ✅ avoid overlap
                                  textOverflow: "ellipsis",
                                },
                                '& .MuiDataGrid-virtualScroller': {
                                  overflow: 'visible !important',
                                },
                              }}
                            >
                              <DataGrid
                                autoHeight
                                hideFooter
                                disableColumnMenu
                                disableColumnSelector
                                disableDensitySelector
                                disableRowSelectionOnClick
                                columnHeaderHeight={0}
                                rows={[flattenedForecastMap[feestage.feestageID] ?? flattenForecasts(feestage)]}
                                columns={monthColumns}
                                getRowId={(row) => row.id ?? feestage.feestageID}
                                isCellEditable={(params) => {
                                  if (readOnly) return false;
                                  const forecast = params.row.forecasts?.find(
                                    (f: any) => dayjs(f.monthDate).format("MMM-YYYY") === params.field
                                  );

                                  if (!forecast) return true;

                                  if (forecast.isInvoiced) return false;
                                  if (forecast.isAutoLock) return false;

                                  return true;
                                }}
                                processRowUpdate={(newRow, oldRow) => {
                                  if (readOnly) return oldRow;
                                  const changedMonth = Object.keys(newRow).find(
                                      (key) => newRow[key] !== oldRow[key] && monthHeaders.includes(key)
                                  );

                                  if (changedMonth) {

                                    const newValue = newRow[changedMonth];
                                    const feestageID = newRow.feestageID;

                                    const latestRow = flattenedForecastMap[feestageID];
                                    const oldValue = latestRow?.[changedMonth] ?? 0;
                                    const outstanding = latestRow?.outstanding ?? 0;

                                    const diff = newValue - oldValue;

                                    // Block if this update causes outstanding to go negative
                                    if (diff > outstanding) {
                                      setToast({
                                        open: true,
                                        message: "Entered value exceeds the outstanding.",
                                        type: "error",
                                      });
                                      return oldRow; // Revert
                                    }

                                    //  Determine if comment modal is needed
                                    const shouldOpenComment =
                                      (oldValue !== 0 && Math.abs(newValue - oldValue) >= 5000) || // Significant change
                                      (oldValue !== 0 && newValue === 0);                          // Drop to zero

                                    if (shouldOpenComment) {
                                      const comment: FeeStageComment = {
                                        id: feestageID,
                                        feeStageReferenceNumber: newRow.feeStageReferenceNumber,
                                        description: newRow.description,
                                        month: changedMonth,
                                        hasComments: latestRow?.forecasts?.some((f: any) => {
                                          const formattedMonth = dayjs(f.monthDate).format('MMM-YYYY');
                                          return formattedMonth === changedMonth && f.isEnabledViewComment;
                                        }) ?? false,
                                        isMandatory: true
                                      };

                                      // Call the comment modal trigger function
                                      handleCommentClick(comment);
                                    }

                                    onForecastChange(newRow.feestageID, changedMonth, newRow[changedMonth]);
                                  }
                                  return newRow;
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}


                  {/* SUBCATEGORY TOTAL */}
                  {openSubcategories[subcategory.subcategoryID] &&
                    (subcategory.feestages?.length ?? 0) > 1 && (
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }} >
                        {/* Expand/collapse column */}
                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("expand", forecastType), 50),
                            minWidth: 50,
                            width: 50,
                            position: "sticky",
                          }}
                        />

                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("second", forecastType), 50),
                            minWidth: 50,
                            width: 50,
                            position: "sticky",
                          }}
                        />
                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                            minWidth: colWidths.code,
                            width: colWidths.code,
                          }}
                        />
                        <TableCell
                          sx={{
                            ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                            minWidth: colWidths.item,
                            width: colWidths.item,
                            fontWeight: "bold",
                            fontSize: "14px",
                          }}
                        >
                          Subcategory Total:
                        </TableCell>

                        {forecastType === ForecastDisplayType.ThirdPartyCost ? (
                          <>
                            <TableCell
                              sx={{
                                ...getStickyStyle(
                                  getLeftOffset("thirdPartyCost", forecastType),
                                  colWidths.cost + colWidths.quantity + colWidths.totalCost
                                ),
                                minWidth: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                                width: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                                fontWeight: "bold",
                              }}
                            >
                              {formatCurrency(
                                subcategory.feestages?.reduce(
                                  (sum, f) => sum + (f.thirdPartyCost || 0),
                                  0
                                )
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                                minWidth: colWidths.outstanding,
                                width: colWidths.outstanding,
                                fontWeight: "bold",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minHeight: 50,   
                                  width: "100%",   
                                  px: 1,          
                                }}
                              >
                                {formatCurrency(
                                  subcategory.feestages?.reduce(
                                    (sum, f) =>
                                      sum +
                                      (flattenedForecastMap[f.feestageID]?.outstanding ??
                                        f.outstanding ??
                                        0),
                                    0
                                  )
                                )}
                              </Box>
                            </TableCell>

                          </>
                        ) : (
                          <>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                                minWidth: colWidths.cost,
                                width: colWidths.cost,
                                fontWeight: "bold",
                              }}
                            >
                              {formatCurrency(
                                subcategory.feestages?.reduce(
                                  (sum, f) => sum + (f.cost || 0),
                                  0
                                )
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("quantity"), colWidths.quantity),
                                minWidth: colWidths.quantity,
                                width: colWidths.quantity,
                                fontWeight: "bold",
                              }}
                            >

                            </TableCell>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("totalCost"), colWidths.totalCost),
                                minWidth: colWidths.totalCost,
                                width: colWidths.totalCost,
                                fontWeight: "bold",
                              }}
                            >
                              {formatCurrency(
                                subcategory.feestages?.reduce(
                                  (sum, f) => sum + (f.totalCost || 0),
                                  0
                                )
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                                minWidth: colWidths.outstanding,
                                width: colWidths.outstanding,
                                fontWeight: "bold",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minHeight: 50,   // ✅ keep consistent height
                                  width: "100%",   // ✅ take full cell width
                                  px: 1,           // ✅ padding like other rows
                                }}
                              >
                                {formatCurrency(
                                  subcategory.feestages?.reduce(
                                    (sum, f) =>
                                      sum +
                                      (flattenedForecastMap[f.feestageID]?.outstanding ??
                                        f.outstanding ??
                                        0),
                                    0
                                  )
                                )}
                              </Box>
                            </TableCell>

                          </>
                        )}
                        {monthColumns.map((col) => {
                          const monthTotal = subcategory.feestages?.reduce((sum, f) => {
                            const forecastRow =
                              flattenedForecastMap[f.feestageID] ?? flattenForecasts(f);

                            return sum + (forecastRow[col.field] || 0);
                          }, 0);

                          return (
                            <TableCell
                              key={col.field}
                              sx={{
                                minWidth: 150,
                                width: 150,
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              {formatCurrency(monthTotal)}
                            </TableCell>
                          );
                        })}

                      </TableRow>

                    )}

                </React.Fragment>
              ))}
          </React.Fragment>
        );
      })}

    </React.Fragment>

  );
};

const CustomForecast = forwardRef(({
  forecastData,
  startDate,
  endDate,
  forecastType,
  readOnly,
}: ModalProps, ref) => {

  const { execute } = useApi();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [updatedRows, setUpdatedRows] = useState<any[]>([]);
  const [flattenedForecastMap, setFlattenedForecastMap] = useState<Record<number, any>>({});
  const [toast, setToast] = useState({ open: false, message: "", type: "success" });
  const [showHiddenMonths, setShowHiddenMonths] = useState(false);
  const [openCommentModal, setOpenCommentModal] = useState(false);
  const [openBulkCommentModal, setOpenBulkCommentModal] = useState(false);
  // const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedComment, setSelectedComment] = useState<{ feestage: FeeStageComment } | null>(null);
  const [outstandingRows, setOutstandingRows] = useState<OutstandingRows[]>([]);

  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [onModalComplete, setOnModalComplete] = useState<((commentAdded: boolean) => void) | null>(null);
  const monthHeaders = generateMonthHeaders(dayjs(startDate), dayjs(endDate));
  
  const [colWidths, setColWidths] = useColWidths(monthHeaders);


  const handleCommentClick = (feestage: FeeStageComment) => {
    setSelectedComment({ feestage });
    setOpenCommentModal(true);
  };

  // const monthColumns = useMemo(() => getMonthColumns(monthHeaders, handleCommentClick),
  //   [monthHeaders, flattenedForecastMap]);

  const visibleMonths = useMemo(() => {
    const columns = getMonthColumns(monthHeaders, handleCommentClick);
    
    const today = dayjs();
   
    if (showHiddenMonths) {
      // Return all columns without filtering
      return columns;
    }

    // Apply filter only when showHiddenMonths is false
    return columns.filter((col) =>
      Object.values(flattenedForecastMap).some((row) =>
        (col as any).isLocked?.(row)
      )
    );
  }, [monthHeaders, flattenedForecastMap, showHiddenMonths, colWidths]);



  const visibleMonthsHeader = visibleMonths
    .map((col) => col.headerName);


  const userId = Number(localStorage.getItem("userId"));

  useEffect(() => {
    const map: Record<number, any> = {};
    forecastData.forEach((proposal) =>
      proposal.categories.forEach((cat) =>
        cat.subcategories.forEach((sub) =>
          sub.feestages.forEach((stage) => {
            map[stage.feestageID] = flattenForecasts(stage);
          })
        )
      )
    );
    setFlattenedForecastMap(map);

    const { totalOutstanding } = calculateTotals(forecastData);
    setTotalOutstanding(totalOutstanding);
  }, [forecastData]);
  const handleForecastChange = (feeStageID: number, month: string, value: number) => {
    const monthDate = parseMonthYearToUTCDate(month)

    // 1. Updated cell for saving in db
    setUpdatedRows((prev) => {
      const index = prev.findIndex(
        (r) => r.feeStageID === feeStageID && r.monthDate === monthDate
      );
      const updated = {
        feeStageID,
        monthDate,
        forecastValue: value,
        createdId: userId,
        displayType: forecastType,
      };
      if (index !== -1) {
        const copy = [...prev];
        copy[index] = updated;
        return copy;
      }
      return [...prev, updated];
    });

    // 2. Update row for UI display
    setFlattenedForecastMap((prev) => {
      const current = prev[feeStageID];
      const oldValue = current?.[month] ?? 0;
      const newOutstanding = (current?.outstanding ?? 0) - (value - oldValue);

      const nextMap = {
        ...prev,
        [feeStageID]: {
          ...current,
          [month]: value,
          outstanding: newOutstanding,
        },
      };

      // Update totalOutstanding after map is ready
      setTotalOutstanding(calculateTotalOutstanding(nextMap));
      return nextMap;
    });
  };

  const paginatedRows = forecastData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const { totalCost, totalQuantity, totalTotalCost, totalThirdPartyCost } = useMemo(
    () => calculateTotals(forecastData),
    [forecastData]
  );

  const handleSaveForecastValue = async (): Promise<boolean> => {

    if (updatedRows.length > 0) {

      // Step 1: Precompute unique feeStageIDs
      const updatedFeeStageIDs = new Set(updatedRows.map(row => Number(row.feeStageID)));


      // Filter fee stages where outstanding > 0
      const rowsWithOutstanding: OutstandingRows[] = Object.entries(flattenedForecastMap)
        .filter(([feeStageID, value]: any) => {
          const { outstanding, isCommentAdded, hasOutstandingComment } = value;

          // Step 1: Must have outstanding > 0
          if (outstanding == 0) return false;

          // Step 2: Must not have comment added already
          if (isCommentAdded) return false;

          // Step 3: Either feeStageID is updated, or no comment exists yet
          if (updatedFeeStageIDs.has(Number(feeStageID))) return true;

          if (!hasOutstandingComment) return true;

          return false;
        })
        .map(([feeStageID, data]: any) => ({
          id: Number(feeStageID),
          feeStageReferenceNumber: data.feeStageReferenceNumber,
          description: data.description,
          cost: data.cost,
          quantity: data.quantity,
          totalCost: data.totalCost,
          thirdPartyCost: data.thirdPartyCost,
          outstanding: data.outstanding,
          notes: ''
        }));

      if (rowsWithOutstanding.length > 0) {
        setOutstandingRows(rowsWithOutstanding);
        setOpenBulkCommentModal(true);

        return new Promise<boolean>((resolve) => {
          setOnModalComplete(() => (commentAdded: boolean) => {
            if (commentAdded) {
              saveForecast().then(() => resolve(true));
            } else {
              // setUpdatedRowsBack(rowsWithOutstanding);
              resolve(false);
            }
          });
        });
      }

      await saveForecast();
      return true;
    }
    else {
      setToast({ open: true, message: "There are no changes in the month or fee stage.", type: "error" });
      return false;
    }
  }

  const saveForecast = async () => {
    const response: ApiResponse<any> = await execute('post', '/Project/SaveForecasts', updatedRows);

    if (!response) return;

    if (response.error) {
      setToast({ open: true, message: "Please try again.", type: "error" });
    } else {
      setToast({ open: true, message: response.data.message, type: "success" });
      setUpdatedRows([])
    }
  };

  const getLeftOffset = (key: keyof typeof colWidths, forecastType?: ForecastDisplayType) => {
    let keys: (keyof typeof colWidths)[];

    if (forecastType === ForecastDisplayType.ThirdPartyCost) {
      // Include the two fixed expand columns in the offset calculation
      keys = ["expand", "second", "code", "item", "thirdPartyCost", "outstanding"];
    } else {
      keys = ["expand", "second", "code", "item", "cost", "quantity", "totalCost", "outstanding"];
    }

    let offset = 0;
    for (const k of keys) {
      if (k === key) break;
      if (k === "thirdPartyCost") {
        offset += colWidths.cost + colWidths.quantity + colWidths.totalCost;
      } else {
        offset += colWidths[k] ?? 50;
      }
    }
    return offset;
  };


  const setUpdatedRowsBack = (outstandingRows: OutstandingRows[]) => {
    setFlattenedForecastMap((prev) => {
      const updatedMap = { ...prev };

      // Step 1: Apply forecastValue from updatedRows
      updatedRows.forEach(({ feeStageID, monthDate, forecastValue }) => {
        const monthKey = dayjs(monthDate).format("MMM-YYYY");

        const current = updatedMap[feeStageID] ?? {};
        const oldValue = current[monthKey] ?? 0;
        const newOutstanding = (current.outstanding ?? 0) - (forecastValue - oldValue);

        updatedMap[feeStageID] = {
          ...current,
          [monthKey]: forecastValue,
          outstanding: newOutstanding,
          hasOutstandingComment: true,
        };
      });

      // Step 2: Set isCommentAdded: true for ALL rows
      Object.keys(updatedMap).forEach((feeStageID) => {
        updatedMap[Number(feeStageID)] = {
          ...updatedMap[Number(feeStageID)],
          isCommentAdded: true,
        };
      });

      // Step 3: Set isCommentAdded = false for outstanding rows
      outstandingRows.forEach(({ id }) => {
        if (!updatedMap[id]) return;

        updatedMap[id] = {
          ...updatedMap[id],
          isCommentAdded: false,
        };
      });

      return updatedMap;
    });
  };

  useImperativeHandle(ref, () => ({
    triggerSave: handleSaveForecastValue,
  }));

  const monthTotals = useMemo(() => {
  const totals: Record<string, number> = {};

  forecastData.forEach((proposal) =>
    proposal.categories.forEach((cat) =>
      cat.subcategories.forEach((sub) =>
        sub.feestages.forEach((stage) => {
          visibleMonths.forEach((col) => {
            const month = col.field; // e.g. "Sep-2025"
            const val = flattenedForecastMap[stage.feestageID]?.[month] ?? 0;
            totals[month] = (totals[month] || 0) + val;
          });
        })
      )
    )
  );

  return totals;
}, [forecastData, visibleMonths, flattenedForecastMap]);


  return (
    <>
      <Paper sx={{ width: '100%', overflow: 'auto', position: 'relative' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" justifyContent="left">
            <IconButton
              color="primary"
              aria-label="toggle months"
              onClick={() => setShowHiddenMonths(prev => !prev)}
            >
              {showHiddenMonths ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <RemoveCircleIcon sx={{ color: '#a3c63d' }} />
                  <Typography variant="body2">Hide Invoiced Months</Typography>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" gap={1}>
                  <AddCircleIcon sx={{ color: '#a3c63d' }} />
                  <Typography variant="body2">Show Invoiced Months</Typography>
                </Box>
              )}
            </IconButton>
          </Box>
        </Box>

        <TableContainer sx={{
          flex: 1,             // expand inside parent
          overflow: 'auto',    // scroll only when needed
          maxHeight: '90%',   // respect parent Box height
        }}>
          <Table stickyHeader sx={{
            tableLayout: "fixed",  // ✅ lock widths so they don’t auto-resize
            width: "100%",
          }}>
            <TableHead>
              <TableRow sx={commonHeaderStyle}>
                <TableCell
                  sx={{
                    ...getStickyStyle(getLeftOffset("expand", forecastType), 50, true),
                    minWidth: 50,
                    width: 50,
                    position: "sticky",
                  }}
                />

                <TableCell
                  sx={{
                    ...getStickyStyle(getLeftOffset("second", forecastType), 50, true),
                    minWidth: 50,
                    width: 50,
                    position: "sticky",
                  }}
                />
                {/* Code */}
                <TableCell
                  sx={{
                    ...getStickyStyle(getLeftOffset("code"), colWidths.code, true),
                    minWidth: colWidths.code,
                    width: colWidths.code,
                    position: "sticky",
                  }}
                >
                  Code
                  <ResizeHandle
                    onResize={(delta) =>
                      setColWidths((prev) => ({
                        ...prev,
                        code: Math.max(prev.code + delta, 50),
                      }))
                    }
                  />
                </TableCell>

                {/* Item */}
                <TableCell
                  sx={{
                    ...getStickyStyle(getLeftOffset("item"), colWidths.item, true),
                    minWidth: colWidths.item,
                    width: colWidths.item,
                    position: "sticky",
                  }}
                >
                  Item
                  <ResizeHandle
                    onResize={(delta) =>
                      setColWidths((prev) => ({
                        ...prev,
                        item: Math.max(prev.item + delta, 50),
                      }))
                    }
                  />
                </TableCell>

                {/* CONDITIONAL COLUMNS */}
                {forecastType === ForecastDisplayType.ThirdPartyCost ? (
                  <>
                    {/* Third Party Cost */}
                    <TableCell
                      sx={{
                        ...getStickyStyle(
                          getLeftOffset("thirdPartyCost", forecastType),
                          colWidths.cost + colWidths.quantity + colWidths.totalCost, true
                        ),
                        minWidth: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                        width: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                        position: "sticky",
                      }}
                    >
                      Third Party Cost
                      <ResizeHandle
                        onResize={(delta) =>
                          setColWidths((prev) => ({
                            ...prev,
                            cost: Math.max(prev.cost + delta, 50),
                            quantity: Math.max(prev.quantity + delta, 50),
                            totalCost: Math.max(prev.totalCost + delta, 50),
                          }))
                        }
                      />
                    </TableCell>

                    {/* Outstanding value */}
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding, true),
                        minWidth: colWidths.outstanding,
                        width: colWidths.outstanding,
                        position: "sticky",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Outstanding value
                      <ResizeHandle
                        onResize={(delta) =>
                          setColWidths((prev) => ({
                            ...prev,
                            outstanding: Math.max(prev.outstanding + delta, 50),
                          }))
                        }
                      />
                    </TableCell>
                  </>
                ) : (
                  <>
                    {/* Cost */}
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("cost"), colWidths.cost, true),
                        minWidth: colWidths.cost,
                        width: colWidths.cost,
                        position: "sticky",
                      }}
                    >
                      Cost
                      <ResizeHandle
                        onResize={(delta) =>
                          setColWidths((prev) => ({
                            ...prev,
                            cost: Math.max(prev.cost + delta, 50),
                          }))
                        }
                      />
                    </TableCell>

                    {/* Quantity */}
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("quantity"), colWidths.quantity, true),
                        minWidth: colWidths.quantity,
                        width: colWidths.quantity,
                        position: "sticky",
                      }}
                    >
                      Quantity
                      <ResizeHandle
                        onResize={(delta) =>
                          setColWidths((prev) => ({
                            ...prev,
                            quantity: Math.max(prev.quantity + delta, 50),
                          }))
                        }
                      />
                    </TableCell>

                    {/* Total Cost */}
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("totalCost"), colWidths.totalCost, true),
                        minWidth: colWidths.totalCost,
                        width: colWidths.totalCost,
                        position: "sticky",
                      }}
                    >
                      Total Cost
                      <ResizeHandle
                        onResize={(delta) =>
                          setColWidths((prev) => ({
                            ...prev,
                            totalCost: Math.max(prev.totalCost + delta, 50),
                          }))
                        }
                      />
                    </TableCell>

                    {/* Outstanding */}
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding, true),
                        minWidth: colWidths.outstanding,
                        width: colWidths.outstanding,
                        position: "sticky",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Outstanding
                      <ResizeHandle
                        onResize={(delta) =>
                          setColWidths((prev) => ({
                            ...prev,
                            outstanding: Math.max(prev.outstanding + delta, 50),
                          }))
                        }
                      />
                    </TableCell>
                  </>
                )}

                {/* Months scrollable */}
                {visibleMonthsHeader.map((month) => (
                  <TableCell key={month} sx={{ minWidth: 150, width: 150 }}>
                    {month}
                  </TableCell>
                ))}

                {/* {visibleMonths.map((col) => (
                  <TableCell key={col.field} sx={{ minWidth: 150, width: 150 }}>
                    {col.headerName}
                  </TableCell>
                ))} */}

              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows?.length ? (
                paginatedRows.map((row) => (
                  <CollapsibleRow
                    key={row.proposalNumber}
                    row={row}
                    monthColumns={visibleMonths}
                    rowStyle={commonRowStyle}
                    monthHeaders={visibleMonthsHeader}
                    onForecastChange={handleForecastChange}
                    flattenedForecastMap={flattenedForecastMap}
                    setToast={setToast}
                    handleCommentClick={handleCommentClick}
                    forecastType={forecastType}
                    readOnly={readOnly}
                    colWidths={colWidths}
                    getLeftOffset={getLeftOffset}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            {/* Footer inside Table */}
            <TableFooter
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 3, // Ensure it's above table body rows
              }}>

              <TableRow sx={{
                '& td': {
                  fontSize: '18px',
                  color: 'black',
                  fontWeight: 'bold',
                  border: '1px solid rgba(0, 0, 0, 0.04)',
                },
              }} >
                <TableCell
                  sx={{
                    ...getStickyStyle(getLeftOffset("expand", forecastType), 50),
                    minWidth: 50,
                    width: 50,
                    position: "sticky",
                  }}
                />

                <TableCell
                  sx={{
                    ...getStickyStyle(getLeftOffset("second", forecastType), 50),
                    minWidth: 50,
                    width: 50,
                    position: "sticky",
                  }}
                />
                <TableCell
                  sx={{
                    ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                    minWidth: colWidths.code,
                    width: colWidths.code,
                  }}
                ></TableCell>
                <TableCell
                  sx={{
                    ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                    minWidth: colWidths.item,
                    width: colWidths.item,
                  }}
                >Total</TableCell>

                {forecastType === ForecastDisplayType.ThirdPartyCost ? (
                  <>
                    <TableCell
                      sx={{
                        ...getStickyStyle(
                          getLeftOffset("thirdPartyCost", forecastType),
                          colWidths.cost + colWidths.quantity + colWidths.totalCost
                        ),
                        minWidth: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                        width: colWidths.cost + colWidths.quantity + colWidths.totalCost,
                        // position: "sticky",
                      }}
                    >{totalThirdPartyCost.toLocaleString()}</TableCell>
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                        minWidth: colWidths.outstanding,
                        width: colWidths.outstanding,
                      }}
                    >{totalOutstanding.toLocaleString()}</TableCell>
                  </>

                ) : (
                  <>
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                        minWidth: colWidths.cost,
                        width: colWidths.cost,
                      }}
                    >{totalCost.toLocaleString()}</TableCell>
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("quantity"), colWidths.quantity),
                        minWidth: colWidths.quantity,
                        width: colWidths.quantity,
                      }}
                    ></TableCell>
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("totalCost"), colWidths.totalCost),
                        minWidth: colWidths.totalCost,
                        width: colWidths.totalCost,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >{totalTotalCost.toLocaleString()}</TableCell>
                    <TableCell
                      sx={{
                        ...getStickyStyle(getLeftOffset("outstanding"), colWidths.outstanding),
                        minWidth: colWidths.outstanding,
                        width: colWidths.outstanding,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >{totalOutstanding.toLocaleString()}</TableCell>
                      {visibleMonths.map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{ minWidth: 150, width: 150 }}
                        >
                          {monthTotals[col.field]?.toLocaleString() ?? 0}
                        </TableCell>
                      ))}
                  </>
                )}
              </TableRow>
            </TableFooter>

          </Table>
        </TableContainer>
      </Paper>

      {openCommentModal && selectedComment && (
        <ThreadForecastCommentModal
          open={openCommentModal}
          onClose={(commentAdded) => {
            if (commentAdded && selectedComment) {
              const feestageID = selectedComment.feestage.id;
              const { month } = selectedComment.feestage;

              setFlattenedForecastMap((prev) => {
                const current = prev[feestageID];
                if (!current) return prev;

                // If Outstanding, update top-level flag
                if (month === 'Outstanding') {
                  return {
                    ...prev,
                    [feestageID]: {
                      ...current,
                      hasOutstandingComment: true,
                      isCommentAdded: true,
                      forecasts: current.forecasts ?? [],
                    },
                  };
                }

                // Else, update specific forecast month
                const updatedForecasts = current.forecasts?.map((f: any) => {
                  const formattedMonth = dayjs(f.monthDate).format('MMM-YYYY');
                  if (formattedMonth === month) {
                    return { ...f, isEnabledViewComment: true };
                  }
                  return f;
                }) ?? [];

                return {
                  ...prev,
                  [feestageID]: {
                    ...current,
                    forecasts: updatedForecasts,
                  },
                };
              });
            }
            setOpenCommentModal(false);
            setSelectedComment(null);
          }}
          feestage={selectedComment.feestage}
          // monthYear={
          //   selectedComment.feestage?.month !== 'Outstanding'
          //     ? parseMonthYearToUTCDate(selectedComment.feestage.month)
          //     : ''
          // }
          forecastType={
            selectedComment.feestage?.month === 'Outstanding'
              ? ForecastDisplayType.OutStanding
              : forecastType
          }
          isBaselineComment={false}
        />
      )}

      {openBulkCommentModal && (
        <AddBulkOutstandingComment
          open={openBulkCommentModal}
          onClose={(commentAdded: boolean) => {
            setOpenBulkCommentModal(false);
            onModalComplete?.(commentAdded); // resolve the pending promise
          }}
          data={outstandingRows}
        />
      )}
      <Toaster
        open={toast.open}
        message={toast.message}
        type={toast.type as "success" | "error"}
        onClose={() =>
          setToast({ ...toast, open: false })
        }
      />
    </>
  );
});

export default CustomForecast;
